import { VALIDATION_FIELD_MAP } from './field-loc';

export interface ReasonExplained {
  /** Plain-Thai sentence shown in the WhyNeedsReview panel. */
  text: string;
  /** Field-key to focus/highlight on click, or null (no jump target). */
  fieldKey: string | null;
}

// RD reason codes (spec §8) → fixed Thai + a field to jump to.
const RD_FIELD: Record<string, string> = {
  RD_NOT_VAT_REGISTERED: 'seller.taxId',
  RD_NAME_MISMATCH: 'seller.name',
  RD_BRANCH_MISMATCH: 'seller.branch',
  RD_VERIFY_UNAVAILABLE: 'seller.taxId',
  RD_INVALID_TAX_ID: 'seller.taxId',
};

// Plain-Thai label per backend validation check `code`
// (see apps/api/.../invoice-validation.ts). Falls back to a generic sentence
// for an unknown check so the raw English code is never shown to the user.
const VALIDATION_TEXT: Record<string, string> = {
  seller_tin_format: 'รูปแบบเลขประจำตัวผู้เสียภาษีของผู้ขายไม่ถูกต้อง',
  is_full_tax_invoice: 'เอกสารไม่ใช่ใบกำกับภาษีแบบเต็มรูป',
  buyer_taxid_match: 'เลขประจำตัวผู้เสียภาษีของผู้ซื้อไม่ตรงกับบริษัท',
  buyer_name_match: 'ชื่อผู้ซื้อไม่ตรงกับบริษัท',
  seller_branch: 'สาขาผู้ขายไม่ครบถ้วน/ไม่ถูกต้อง',
  footing_lines: 'ผลรวมรายการสินค้าไม่ตรงกับยอดรวม',
  footing_total: 'ยอดรวมเอกสารไม่ถูกต้อง',
  vat_rate: 'อัตราภาษีมูลค่าเพิ่มไม่ถูกต้อง',
  bevdate_converted: 'วันที่เอกสารถูกแปลงจาก พ.ศ. — โปรดตรวจสอบ',
};

// Severity prefix shown ahead of a failed validation message.
const VALIDATION_SEVERITY_TH: Record<string, string> = {
  CRITICAL: 'ตรวจสอบไม่ผ่าน (สำคัญ)',
  WARNING: 'ตรวจสอบไม่ผ่าน (คำเตือน)',
};

// Translate one detectAnomaly/preview reason code into Thai + jump target.
// Codes may carry an inline payload, e.g. RD_NAME_MISMATCH(score=0.74) or
// LOW_CONFIDENCE:invoiceNo. The backend emits validation codes as the
// two-segment shape VALIDATION_CRITICAL:<code> / VALIDATION_WARNING:<code>
// (doc-ingest.service.ts) — <code> (not the severity) is the field-map key.
// Never throws.
export function reasonToThai(code: string): ReasonExplained {
  const base = code.split('(')[0] ?? code; // strip "(score=…)"
  const scoreMatch = /score=([0-9.]+)/.exec(code);
  const score = scoreMatch ? scoreMatch[1] : null;

  if (base.startsWith('RD_')) {
    const fieldKey = RD_FIELD[base] ?? 'seller.taxId';
    const text =
      base === 'RD_NOT_VAT_REGISTERED'
        ? 'ไม่พบการจดทะเบียนภาษีมูลค่าเพิ่มของผู้ขายกับกรมสรรพากร'
        : base === 'RD_NAME_MISMATCH'
          ? `ชื่อผู้ขายไม่ตรงกับทะเบียนกรมสรรพากร${score ? ` (คะแนนความตรง ${score})` : ''}`
          : base === 'RD_BRANCH_MISMATCH'
            ? 'สาขาผู้ขายไม่ตรงกับทะเบียนกรมสรรพากร'
            : base === 'RD_INVALID_TAX_ID'
              ? 'เลขประจำตัวผู้เสียภาษีของผู้ขายไม่ถูกต้อง'
              : 'ตรวจสอบกับกรมสรรพากรไม่ได้ในขณะนี้ — ระบบจะตรวจซ้ำให้';
    return { text, fieldKey };
  }

  if (base.startsWith('LOW_CONFIDENCE:')) {
    const field = base.slice('LOW_CONFIDENCE:'.length);
    return {
      text: `ความเชื่อมั่นต่ำที่ "${field}" — โปรดตรวจสอบค่าที่อ่านได้`,
      fieldKey: field || null,
    };
  }

  if (base === 'KEY_FIELD_EDITED') {
    return { text: 'มีการแก้ไขฟิลด์สำคัญหลังการอ่าน', fieldKey: null };
  }
  if (base === 'VAT_CREDIT_OVERRIDE') {
    return { text: 'มีการเปลี่ยนสถานะภาษีซื้อด้วยตนเอง', fieldKey: 'vatAmount' };
  }

  // VALIDATION_<SEVERITY>:<code> — the backend always prefixes the severity
  // (VALIDATION_CRITICAL:vat_rate / VALIDATION_WARNING:buyer_name_match), so the
  // field-map key is the SECOND segment, not whatever follows "VALIDATION_".
  const validationMatch = /^VALIDATION_(CRITICAL|WARNING):(.+)$/.exec(base);
  if (validationMatch) {
    const severity = validationMatch[1] ?? '';
    const checkCode = validationMatch[2] ?? '';
    const label = VALIDATION_TEXT[checkCode] ?? 'พบข้อมูลที่ต้องตรวจสอบในเอกสาร';
    const prefix = VALIDATION_SEVERITY_TH[severity] ?? 'ตรวจสอบไม่ผ่าน';
    return {
      text: `${prefix}: ${label}`,
      fieldKey: VALIDATION_FIELD_MAP[checkCode] ?? null,
    };
  }

  return { text: `ต้องตรวจสอบ: ${code}`, fieldKey: null };
}
